/**
 * Design Tokens — Teia de Eventos
 *
 * Paleta extraída diretamente do logo da marca:
 *  - Navy (#183C54): cor primária — tipografia, fundos escuros
 *  - Steel (#186C9C): cor secundária — gradientes, hover
 *  - Cyan (#00A8F0): cor de destaque — CTAs, microinterações
 *
 * Tudo aqui é tipado e consumido via styled-components ThemeProvider.
 */

const colors = {
  /** Azul marinho — cor primária da marca (extraída do logo). */
  navy: {
    50: '#E9F0F5',
    100: '#C5D6E1',
    200: '#8AABC0',
    300: '#5B89A3',
    400: '#3A6F8E',
    500: '#235978',
    600: '#1E4A66',
    700: '#183C54', // ← Cor principal do logo
    800: '#142A3D',
    900: '#0C1F2E',
    950: '#06121C',
  },
  /** Azul aço — cor secundária (extraída das facetas médias do logo). */
  steel: {
    50: '#E6F1F8',
    100: '#C2DCEC',
    200: '#90BFDC',
    300: '#5A9FCA',
    400: '#3585B5',
    500: '#186C9C', // ← Cor secundária do logo
    600: '#156496',
    700: '#125580',
    800: '#0F4063',
    900: '#0A2D44',
  },
  /** Ciano vibrante — cor de destaque (extraída dos brilhos do logo). */
  cyan: {
    50: '#E1F2FD',
    100: '#B5E2FA',
    200: '#7FCEF6',
    300: '#3BBAF3',
    400: '#00A8F0', // ← Cor de destaque do logo
    500: '#0094D6',
    600: '#0085BB',
    700: '#006B96',
    800: '#004F6E',
    900: '#003040',
  },
  /** Neutros frios derivados do navy para coesão cromática. */
  neutral: {
    0: '#FFFFFF',
    50: '#F7F9FC',
    100: '#EEF2F7',
    200: '#DDE4EC',
    300: '#C2CCD8',
    400: '#94A2B3',
    500: '#677687',
    600: '#4A5868',
    700: '#374252',
    800: '#212A37',
    900: '#0F1620',
    1000: '#000000',
  },
  /** Estados semânticos. */
  feedback: {
    success: '#16A34A',
    warning: '#F59E0B',
    error: '#DC2626',
    info: '#00A8F0',
  },
} as const;

const semantic = {
  /** Cores semânticas — usar SEMPRE estas no componente, nunca as cores cruas. */
  background: {
    primary: colors.neutral[0],
    secondary: colors.neutral[50],
    tertiary: colors.neutral[100],
    inverse: colors.navy[700],
    inverseDeep: colors.navy[900],
  },
  surface: {
    raised: colors.neutral[0],
    sunken: colors.neutral[50],
    overlay: 'rgba(12, 31, 46, 0.72)',
    overlayLight: 'rgba(12, 31, 46, 0.32)',
  },
  text: {
    primary: colors.navy[900],
    secondary: colors.navy[700],
    tertiary: colors.neutral[600],
    muted: colors.neutral[500],
    inverse: colors.neutral[0],
    inverseMuted: colors.neutral[200],
    accent: colors.cyan[400],
  },
  border: {
    subtle: colors.neutral[200],
    default: colors.neutral[300],
    strong: colors.navy[700],
    accent: colors.cyan[400],
  },
  brand: {
    primary: colors.navy[700],
    primaryHover: colors.navy[600],
    secondary: colors.steel[500],
    secondaryHover: colors.steel[400],
    accent: colors.cyan[400],
    accentHover: colors.cyan[300],
  },
} as const;

const fonts = {
  /** Fonte primária — Inter (carregada via @import no global). */
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  /** Fonte de display — Inter com tracking ajustado. Pode trocar para uma display dedicada futuramente. */
  display: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  /** Mono para detalhes pontuais. */
  mono: "'JetBrains Mono', ui-monospace, monospace",
} as const;

const fontSizes = {
  xs: '0.75rem', //   12px
  sm: '0.875rem', //  14px
  base: '1rem', //    16px
  md: '1.0625rem', // 17px
  lg: '1.125rem', //  18px
  xl: '1.25rem', //   20px
  '2xl': '1.5rem', // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem', //   48px
  '6xl': '3.75rem', // 60px
  '7xl': '4.5rem', // 72px
  '8xl': '6rem', //   96px
  '9xl': '8rem', //   128px
} as const;

const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

const lineHeights = {
  none: 1,
  tight: 1.1,
  snug: 1.25,
  normal: 1.5,
  relaxed: 1.65,
  loose: 1.85,
} as const;

const letterSpacings = {
  tighter: '-0.03em',
  tight: '-0.02em',
  snug: '-0.01em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
} as const;

const spacing = {
  0: '0',
  px: '1px',
  '0.5': '0.125rem', // 2px
  1: '0.25rem', //     4px
  2: '0.5rem', //      8px
  3: '0.75rem', //     12px
  4: '1rem', //        16px
  5: '1.25rem', //     20px
  6: '1.5rem', //      24px
  7: '1.75rem', //     28px
  8: '2rem', //        32px
  10: '2.5rem', //     40px
  12: '3rem', //       48px
  14: '3.5rem', //     56px
  16: '4rem', //       64px
  20: '5rem', //       80px
  24: '6rem', //       96px
  28: '7rem', //       112px
  32: '8rem', //       128px
  40: '10rem', //      160px
  48: '12rem', //      192px
  56: '14rem', //      224px
  64: '16rem', //      256px
} as const;

const radii = {
  none: '0',
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '20px',
  xl: '28px',
  '2xl': '40px',
  full: '9999px',
} as const;

/** Sombras baseadas em navy escuro para coesão cromática. */
const shadows = {
  xs: '0 1px 2px rgba(12, 31, 46, 0.04)',
  sm: '0 2px 4px rgba(12, 31, 46, 0.06), 0 1px 2px rgba(12, 31, 46, 0.04)',
  md: '0 4px 12px rgba(12, 31, 46, 0.08), 0 2px 4px rgba(12, 31, 46, 0.04)',
  lg: '0 12px 32px rgba(12, 31, 46, 0.12), 0 4px 8px rgba(12, 31, 46, 0.06)',
  xl: '0 24px 64px rgba(12, 31, 46, 0.16), 0 8px 16px rgba(12, 31, 46, 0.08)',
  '2xl': '0 40px 96px rgba(12, 31, 46, 0.24), 0 16px 32px rgba(12, 31, 46, 0.12)',
  glow: '0 0 32px rgba(0, 168, 240, 0.32)',
  glowStrong: '0 0 48px rgba(0, 168, 240, 0.48)',
  inset: 'inset 0 1px 2px rgba(12, 31, 46, 0.06)',
} as const;

/**
 * Easings cinemáticas — cuidadosamente curadas para sensação premium.
 * Usar SEMPRE estes valores, nunca cubic-bezier inline.
 */
const easings = {
  /** Padrão suave — para a maioria das transições. */
  smooth: [0.25, 0.1, 0.25, 1] as const,
  /** Saída expo — cinematográfico, ideal para reveals on-scroll. */
  smoothOut: [0.16, 1, 0.3, 1] as const,
  /** Quart in-out — transições entre estados. */
  smoothInOut: [0.65, 0, 0.35, 1] as const,
  /** Overshoot sutil — entrada de elementos com leveza. */
  spring: [0.34, 1.56, 0.64, 1] as const,
  /** Material standard — microinterações rápidas. */
  swift: [0.4, 0, 0.2, 1] as const,
  /** Anticipação — para hover de botões. */
  anticipate: [0.68, -0.55, 0.265, 1.55] as const,
} as const;

const durations = {
  instant: 0.1,
  fast: 0.2,
  base: 0.3,
  slow: 0.5,
  slower: 0.8,
  cinematic: 1.2,
} as const;

const breakpoints = {
  mobile: 480,
  tablet: 768,
  laptop: 1024,
  desktop: 1280,
  wide: 1536,
} as const;

const zIndex = {
  hide: -1,
  base: 0,
  raised: 10,
  dropdown: 100,
  sticky: 200,
  overlay: 300,
  modal: 400,
  popover: 500,
  toast: 600,
  tooltip: 700,
  max: 9999,
} as const;

const layout = {
  /** Largura máxima do container principal. */
  maxWidth: '1400px',
  /** Padding lateral responsivo. */
  containerPadding: {
    mobile: spacing[5],
    tablet: spacing[8],
    desktop: spacing[12],
  },
  /** Altura do header fixo. */
  headerHeight: '80px',
  /** Altura do header em mobile. */
  headerHeightMobile: '64px',
} as const;

export const theme = {
  colors,
  semantic,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  letterSpacings,
  spacing,
  radii,
  shadows,
  easings,
  durations,
  breakpoints,
  zIndex,
  layout,
} as const;

export type Theme = typeof theme;
